import { useEffect, useState } from 'react';
import client from '../api/client';

/**
 * Modal die — vanuit een opgeslagen Configuration — een
 * supplier_quote_request aanmaakt en de RFQ-PDF in de browser opent.
 */
export default function RequestSupplierQuoteModal({ configurationId, onClose }) {
    const [suppliers, setSuppliers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [supplierId, setSupplierId] = useState('');
    const [contactId, setContactId] = useState('');
    const [reference, setReference] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [delivery, setDelivery] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [created, setCreated] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);

    useEffect(() => {
        client.get('/suppliers', { params: { is_active: 1, per_page: 100 } })
            .then((r) => setSuppliers(r.data.items || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!supplierId) { setContacts([]); return; }
        client.get(`/suppliers/${supplierId}/contacts`)
            .then((r) => {
                setContacts(r.data || []);
                const primary = (r.data || []).find((c) => c.is_primary == 1);
                if (primary) setContactId(primary.id);
            })
            .catch(() => {});
    }, [supplierId]);

    const submit = async () => {
        if (!supplierId) return;
        setLoading(true);
        setError(null);
        try {
            // 1. Maak RFQ aan.
            const { data: rfq } = await client.post('/supplier-quote-requests', {
                configuration_id: configurationId,
                supplier_id: Number(supplierId),
                supplier_contact_id: contactId ? Number(contactId) : null,
                reference,
                quantity,
                desired_delivery_date: delivery,
                message,
            });
            setCreated(rfq);

            // 2. Genereer spec-PDF.
            const { data: pdf } = await client.post(`/supplier-quote-requests/${rfq.id}/spec-pdf`);
            setPdfUrl(pdf.file_url);

            // 3. Markeer als verstuurd zodra de gebruiker het mailprogramma opent.
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Onbekende fout');
        } finally {
            setLoading(false);
        }
    };

    const openMailClient = async () => {
        if (!created) return;
        const supplier = suppliers.find((s) => s.id == supplierId);
        const contact = contacts.find((c) => c.id == contactId);
        const to = contact?.email || supplier?.default_contact_email || '';
        const subject = `RFQ ${reference || `#${created.id}`} — Aanvraag offerte`;
        const body = `${message}\n\nSpec-PDF (open de bijgevoegde link): ${pdfUrl || ''}`;
        const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = url;

        // Markeer RFQ als verzonden in de backend.
        await client.post(`/supplier-quote-requests/${created.id}/mark-sent`, { send_method: 'local_mailto' });
    };

    return (
        <div className="ast-modal-overlay" onClick={onClose}>
            <div className="ast-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ast-modal__header">
                    <h2>Offerte aanvragen bij leverancier</h2>
                    <button className="ast-modal__close" onClick={onClose}>×</button>
                </div>

                <div className="ast-modal__body">
                    {!created ? (
                        <>
                            <label className="ast-form__label">
                                Leverancier
                                <select
                                    value={supplierId}
                                    onChange={(e) => setSupplierId(e.target.value)}
                                    className="ast-form__select"
                                >
                                    <option value="">— Kies leverancier —</option>
                                    {suppliers.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </label>

                            {contacts.length > 0 && (
                                <label className="ast-form__label">
                                    Contact
                                    <select
                                        value={contactId}
                                        onChange={(e) => setContactId(e.target.value)}
                                        className="ast-form__select"
                                    >
                                        <option value="">— Standaard email —</option>
                                        {contacts.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}{c.role ? ` (${c.role})` : ''}</option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            <label className="ast-form__label">
                                Referentie
                                <input
                                    type="text"
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    className="ast-form__input"
                                    placeholder="Bv. RFQ-2026-001"
                                />
                            </label>

                            <div style={{ display: 'flex', gap: 12 }}>
                                <label className="ast-form__label" style={{ flex: 1 }}>
                                    Aantal voertuigen
                                    <input
                                        type="number"
                                        min={1}
                                        value={quantity}
                                        onChange={(e) => setQuantity(Number(e.target.value))}
                                        className="ast-form__input"
                                    />
                                </label>
                                <label className="ast-form__label" style={{ flex: 1 }}>
                                    Gewenste leverdatum
                                    <input
                                        type="text"
                                        value={delivery}
                                        onChange={(e) => setDelivery(e.target.value)}
                                        className="ast-form__input"
                                        placeholder="Bv. Q3 2026"
                                    />
                                </label>
                            </div>

                            <label className="ast-form__label">
                                Bericht aan leverancier
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={4}
                                    className="ast-form__textarea"
                                    placeholder="Aanvullende informatie of vragen..."
                                />
                            </label>

                            {error && <div className="ast-modal__error">{error}</div>}
                        </>
                    ) : (
                        <div>
                            <p>RFQ aangemaakt — referentie <strong>{created.reference || `#${created.id}`}</strong>.</p>
                            {pdfUrl && (
                                <p>
                                    <a href={pdfUrl} target="_blank" rel="noreferrer" className="ast-modal__link">
                                        Open de spec-PDF
                                    </a> en voeg deze als bijlage toe aan je email.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="ast-modal__footer">
                    {!created ? (
                        <>
                            <button className="ast-btn ast-btn--secondary" onClick={onClose}>Annuleren</button>
                            <button
                                className="ast-btn ast-btn--primary"
                                onClick={submit}
                                disabled={loading || !supplierId}
                            >
                                {loading ? 'Bezig...' : 'Aanvraag aanmaken'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="ast-btn ast-btn--secondary" onClick={onClose}>Sluiten</button>
                            <button className="ast-btn ast-btn--primary" onClick={openMailClient}>
                                Open mail-client en verzenden
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
